import Vue from 'vue'
import gql from '@my/graphql'
import gqlOrder from '@/graphql/order'
import gqlChange from '@/graphql/change'
import { recordProductQuantity } from '@/helpers/productQuantity'

function intervalSecures (value) {
  if (value <= 150000) return 50000
  if (value <= 400000) return 100000
  return 200000
}

function getPackageName (record) {
  if (record.brand === 'bassica') return 'xs'
  if (record.brand === 'sahet') {
    const value = record.value || record.total || 0
    if (value > 0 && value <= 250000) return 's'
    if (value > 250000 && value <= 400000) return 'm'
    if (value > 400000 && value <= 700000) return 'l'
    if (value > 700000) return 'xl'
  }
}

export function enrichMassRecord (doc, preserve = {}) {
  const record = { ...doc }
  const isChange = !!record.change

  record.value = isChange ? 50000 : (record.total || 0)
  record.quantity = recordProductQuantity(record)
  record.declared = preserve.declared !== undefined
    ? preserve.declared
    : (isChange ? 50000 : intervalSecures(record.total || 0))
  record.showDeclared = preserve.showDeclared !== undefined ? preserve.showDeclared : true
  record.checked = preserve.checked !== undefined ? preserve.checked : false
  record.package = preserve.package !== undefined ? preserve.package : getPackageName(record)
  record.packageDimensions = preserve.packageDimensions
  record.packages = preserve.packages
  record.error = preserve.error !== undefined ? preserve.error : ''
  record.success = preserve.success !== undefined ? preserve.success : ''
  record.coordinadoraCost = preserve.coordinadoraCost

  if (isChange) {
    record.order = 'c' + record.change
  }

  return record
}

const MASS_ORDER_SYNC_KEYS = [
  'status',
  'quantity',
  'createdAt',
  'name',
  'location',
  'department',
  'address',
  'phones',
  'brand',
  'remitData',
  'remitDataGenerated',
  'dataLocked',
  'hasNoveltyData',
  'noveltyData',
  'methodPay',
  'conveyor',
  'weliveryEnabled',
  'userPayConveyor',
  'total'
]

const MASS_CHANGE_SYNC_KEYS = [
  'status',
  'createdAt',
  'name',
  'location',
  'department',
  'address',
  'phones',
  'brand',
  'remitData',
  'remitDataGenerated',
  'dataLocked',
  'methodPay',
  'payManual'
]

export function applyMassRecordSync (target, dataUpdated, type) {
  if (!target || !dataUpdated) return

  const keys = type === 'change' ? MASS_CHANGE_SYNC_KEYS : MASS_ORDER_SYNC_KEYS

  keys.forEach(key => {
    if (dataUpdated[key] === undefined) return

    if (key === 'remitDataGenerated' || key === 'dataLocked' || key === 'hasNoveltyData') {
      Vue.set(target, key, !!dataUpdated[key])
      return
    }

    Vue.set(target, key, dataUpdated[key])
  })

  if (dataUpdated.total !== undefined && !target.change) {
    Vue.set(target, 'value', dataUpdated.total || 0)
  }

  if (type === 'change' && dataUpdated.newProducts !== undefined) {
    Vue.set(target, 'quantity', recordProductQuantity({ change: target.change, newProducts: dataUpdated.newProducts }))
  }
}

function findMassRecordIndex (state, recordKey) {
  return state.orders.findIndex(item => item.order === recordKey)
}

function findMassRecordByOrder (state, order) {
  return state.orders.find(item => item.order === order)
}

function findMassRecordByChange (state, change) {
  return state.orders.find(item => item.change === change)
}

export default {
  namespaced: true,

  state: {
    orders: [],
    isLoading: false
  },

  mutations: {
    setMassLoading (state, loading) {
      state.isLoading = loading
    },

    setMassOrders (state, orders) {
      state.orders = orders
    },

    upsertMassOrder (state, record) {
      const index = findMassRecordIndex(state, record.order)

      if (index >= 0) {
        Vue.set(state.orders, index, record)
        return
      }

      state.orders.unshift(record)
    },

    removeAllMassOrders (state) {
      state.orders = []
    },

    removeMassOrder (state, orderKey) {
      const index = state.orders.findIndex(r => r.order === orderKey)
      if (index >= 0) state.orders.splice(index, 1)
    },

    setMassOrderChecked (state, { order, value }) {
      const found = findMassRecordByOrder(state, order)
      if (!found) return
      Vue.set(found, 'checked', value)
    },

    setMassAllChecked (state, value) {
      state.orders.forEach(record => {
        Vue.set(record, 'checked', value)
      })
    },

    updateMassOrderFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.order) return

      const found = findMassRecordByOrder(state, dataUpdated.order)
      if (found) {
        applyMassRecordSync(found, dataUpdated, 'order')
      }
    },

    updateMassChangeFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.change) return

      const found = findMassRecordByChange(state, dataUpdated.change)
      if (found) {
        applyMassRecordSync(found, dataUpdated, 'change')
      }
    },

    updateMassDeliveryFromSocket (state, dataUpdated) {
      if (!dataUpdated || !dataUpdated.delivery) return

      for (const record of state.orders) {
        if (!record.deliveries || !record.deliveries.length) continue

        const deliveryDoc = record.deliveries.find(item => item.delivery === dataUpdated.delivery)

        if (deliveryDoc) {
          Object.keys(dataUpdated).forEach(key => {
            if (dataUpdated[key] !== undefined) {
              Vue.set(deliveryDoc, key, dataUpdated[key])
            }
          })
          return
        }
      }
    },

    addMassDeliveryFromSocket (state, deliveryDoc) {
      if (!deliveryDoc || !deliveryDoc.delivery) return

      const order = deliveryDoc.order
      const change = deliveryDoc.change

      if (change) {
        const found = findMassRecordByChange(state, change)
        if (!found) return
        if (!found.deliveries) Vue.set(found, 'deliveries', [])
        if (found.deliveries.some(item => item.delivery === deliveryDoc.delivery)) return
        found.deliveries.push(deliveryDoc)
        return
      }

      if (!order) return

      const found = findMassRecordByOrder(state, order)
      if (!found) return
      if (!found.deliveries) Vue.set(found, 'deliveries', [])
      if (found.deliveries.some(item => item.delivery === deliveryDoc.delivery)) return
      found.deliveries.push(deliveryDoc)
    }
  },

  actions: {
    async fetchMassOrder ({ commit, state }, { order, sound = true }) {
      const response = await gql.query(gqlOrder.GET_BY_ORDER_ADMIN, { order }, false)
      const orderFound = response.data.getByOrderAdmin

      if (!orderFound) {
        return { success: false, notFound: true }
      }

      const existing = findMassRecordByOrder(state, orderFound.order)
      const alreadyLoaded = !!existing
      const preserve = existing
        ? {
          checked: existing.checked,
          package: existing.package,
          packageDimensions: existing.packageDimensions,
          packages: existing.packages,
          declared: existing.declared,
          showDeclared: existing.showDeclared,
          coordinadoraCost: existing.coordinadoraCost,
          error: existing.error,
          success: existing.success
        }
        : {}

      commit('upsertMassOrder', enrichMassRecord(orderFound, preserve))
      return { success: true, sound, alreadyLoaded }
    },

    async fetchMassChange ({ commit, state }, { changeCode, sound = true }) {
      const change = changeCode.replace('c', '').trim()
      const response = await gql.query(gqlChange.GET_BY_CHANGE_ADMIN, { change }, false)
      const orderFound = response.data.getByChangeAdmin

      if (!orderFound) {
        return { success: false, notFound: true }
      }

      const existing = findMassRecordByChange(state, orderFound.change)
      const alreadyLoaded = !!existing
      const preserve = existing
        ? {
          checked: existing.checked,
          package: existing.package,
          packageDimensions: existing.packageDimensions,
          packages: existing.packages,
          declared: existing.declared,
          showDeclared: existing.showDeclared,
          coordinadoraCost: existing.coordinadoraCost,
          error: existing.error,
          success: existing.success
        }
        : {}

      commit('upsertMassOrder', enrichMassRecord(orderFound, preserve))

      return { success: true, sound, alreadyLoaded }
    },

    async loadFromSession ({ commit, dispatch, state }) {
      let orderIds = JSON.parse(sessionStorage.getItem('ordersMass'))
      const packages = JSON.parse(sessionStorage.getItem('packagesMass')) || []
      const declared = JSON.parse(sessionStorage.getItem('declaredMass')) || []
      const costs = JSON.parse(sessionStorage.getItem('costsMass')) || []
      const dimensions = JSON.parse(sessionStorage.getItem('dimensionsMass')) || []
      const packageLists = JSON.parse(sessionStorage.getItem('packagesListMass')) || []

      if (!orderIds || !orderIds.length) return

      commit('setMassLoading', true)

      orderIds = orderIds.reverse()

      for (const orderId of orderIds) {
        const firstLetter = orderId[0].toLowerCase()
        if (firstLetter === 's') {
          await dispatch('fetchMassOrder', { order: orderId, sound: false })
        }
        if (firstLetter === 'c') {
          await dispatch('fetchMassChange', { changeCode: orderId, sound: false })
        }
      }

      state.orders.forEach((order, index) => {
        if (packages[index]) Vue.set(order, 'package', packages[index])
        if (declared[index]) Vue.set(order, 'declared', declared[index])
        if (costs[index] !== undefined) Vue.set(order, 'coordinadoraCost', costs[index])
        if (dimensions[index]) Vue.set(order, 'packageDimensions', dimensions[index])
        if (packageLists[index]) Vue.set(order, 'packages', packageLists[index])
      })

      commit('setMassLoading', false)
    },

    clearAll ({ commit }) {
      commit('removeAllMassOrders')
      sessionStorage.removeItem('ordersMass')
      sessionStorage.removeItem('packagesMass')
      sessionStorage.removeItem('declaredMass')
      sessionStorage.removeItem('costsMass')
      sessionStorage.removeItem('dimensionsMass')
      sessionStorage.removeItem('packagesListMass')
    },

    persistSession ({ state }) {
      const orders = state.orders.map(order => order.order)
      const packages = state.orders.map(order => order.package)
      const declared = state.orders.map(order => order.declared)
      const costs = state.orders.map(order => order.coordinadoraCost || null)
      const dimensions = state.orders.map(order => order.packageDimensions || null)
      const packageLists = state.orders.map(order => (order.packages && order.packages.length > 1) ? order.packages : null)
      sessionStorage.setItem('ordersMass', JSON.stringify(orders))
      sessionStorage.setItem('packagesMass', JSON.stringify(packages))
      sessionStorage.setItem('declaredMass', JSON.stringify(declared))
      sessionStorage.setItem('costsMass', JSON.stringify(costs))
      sessionStorage.setItem('dimensionsMass', JSON.stringify(dimensions))
      sessionStorage.setItem('packagesListMass', JSON.stringify(packageLists))
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/mass.js